import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/react';
import {
  deleteUserPacketTemplate,
  getUserPacketTemplates,
} from '../../services/UserPacketTemplateService';
import { ThreeDotMenu } from '../Packet/Components/ThreeDotMenu';
import { Mixpanel } from '../../services/Mixpanel';
import { AddToTemplatesModal } from '../../components/modals/AddToTemplatesModal';
import { ShareTemplateModal } from '../../components/modals/ShareTemplateModal';
import { useUnlimitedContext } from '../Unlimited/contexts/UnlimitedContext';
import { createPortal } from 'react-dom';
import SidebarSection, { SIDEBAR_SECTION_ITEM_CLASSES } from '../Base/components/SidebarSection';
import { DropdownButton } from '../MyResources/PacketCard';
import { DeleteTemplateModal } from '../Unlimited/UnlimitedSidebar/DeleteTemplateModal';

/**
 * Renders a collapsible "Templates" resources section into the AppSidebar portal slot.
 */
export const TemplatesSidebar = () => {
  const { template } = useUnlimitedContext();
  const { selectedTemplate, setSelectedTemplate, refetchTemplatesTrigger } = template;

  const { isSignedIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [templateToShare, setTemplateToShare] = useState(null);

  // Handle selecting a template
  const handleSelectTemplate = (template) => {
    if (selectedTemplate?.id === template.id) {
      // Deselect if already selected
      Mixpanel.track('Unlimited Template Deselected', {
        template_id: template.id,
        template_title: template.title,
      });
      setSelectedTemplate(null);
    } else {
      Mixpanel.track('Unlimited Template Selected', {
        template_id: template.id,
        template_title: template.title,
      });
      setSelectedTemplate(template);
    }
  };

  // Handle starting edit
  const handleStartEdit = (template, e) => {
    e.stopPropagation();
    Mixpanel.track('Unlimited Template Edit Started', {
      template_id: template.id,
      template_title: template.title,
    });
    setTemplateToEdit(template);
    setShowEditModal(true);
  };

  // Handle template updated from modal
  const handleTemplateUpdated = (updatedTemplate) => {
    setTemplates((prev) => prev.map((t) => (t.id === updatedTemplate.id ? updatedTemplate : t)));
    // Update selected template if it was edited
    if (selectedTemplate?.id === updatedTemplate.id) {
      setSelectedTemplate(updatedTemplate);
    }
    setTemplateToEdit(null);
  };

  // Handle delete template - show confirmation
  const handleDeleteTemplate = (template, e) => {
    e.stopPropagation();
    Mixpanel.track('Unlimited Template Delete Clicked', {
      template_id: template.id,
      template_title: template.title,
    });
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  // Confirm delete template
  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    Mixpanel.track('Unlimited Template Deleted', {
      template_id: templateToDelete.id,
      template_title: templateToDelete.title,
    });
    try {
      await deleteUserPacketTemplate(templateToDelete.id);
      setTemplates((prev) => prev.filter((t) => t.id !== templateToDelete.id));
      // Deselect if the deleted template was selected
      if (selectedTemplate?.id === templateToDelete.id) {
        setSelectedTemplate(null);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
    setTemplateToDelete(null);
    setShowDeleteModal(false);
  };

  // Handle share template
  const handleShareTemplate = (template, e) => {
    e.stopPropagation();
    Mixpanel.track('Unlimited Template Share Clicked', {
      template_id: template.id,
      template_title: template.title,
    });
    setTemplateToShare(template);
    setShowShareModal(true);
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setTemplateToDelete(null);
    setShowDeleteModal(false);
  };

  const fetchPackets = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getUserPacketTemplates({});
      setTemplates(response.data.results || []);
      if (refetchTemplatesTrigger > 0) {
        setOpen(true);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  }, [refetchTemplatesTrigger]);

  useEffect(() => {
    if (!isSignedIn) {
      setIsLoading(false);
      setTemplates([]);
      return;
    }
    fetchPackets();
  }, [fetchPackets, isSignedIn, refetchTemplatesTrigger]);

  if (!isSignedIn) return null;
  if (!isLoading && templates.length === 0) return null;

  const portalTarget = document.getElementById('app-sidebar-page-content');
  if (!portalTarget) return null;

  return createPortal(
    <SidebarSection title='Templates' open={open} setOpen={setOpen} tutorial='templates-sidebar'>
      {isLoading ? (
        <div className='px-4 py-4 text-center text-sm text-gray-600'>Loading...</div>
      ) : (
        <ul>
          {templates.map((template) => {
            return (
              <li key={template.id} className='ml-2 relative group'>
                <div
                  tabIndex={0}
                  role='button'
                  onClick={() => handleSelectTemplate(template)}
                  className={`${SIDEBAR_SECTION_ITEM_CLASSES} group-hover:pr-12`}>
                  {template.title || 'Untitled'}
                </div>
                <div className='absolute top-[2px] right-[2px] flex items-center flex-shrink-0 transition-opacity opacity-0 group-hover:opacity-100'>
                  <ThreeDotMenu
                    data-allow-click-when-free-user
                    direction='right'
                    location='Unlimited Sidebar: Template'
                    buttonClassName='p-1.5 hover:bg-gray-300 rounded transition-colors'
                    buttons={[
                      <DropdownButton
                        key='share'
                        label='Share'
                        onClick={(e) => handleShareTemplate(template, e)}
                        aria-label='Share Template'
                      />,
                      <DropdownButton
                        key='edit'
                        label='Edit'
                        onClick={(e) => handleStartEdit(template, e)}
                        aria-label='Edit Template'
                      />,
                      <DropdownButton
                        key='delete'
                        label='Delete'
                        onClick={(e) => handleDeleteTemplate(template, e)}
                        aria-label='Delete Template'
                      />,
                    ]}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <AddToTemplatesModal
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        templateToEdit={templateToEdit}
        onTemplateUpdated={handleTemplateUpdated}
        source='Unlimited Sidebar'
      />
      <DeleteTemplateModal
        templateName={templateToDelete?.title}
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      <ShareTemplateModal
        showModal={showShareModal}
        setShowModal={(val) => {
          setShowShareModal(val);
          if (!val) setTemplateToShare(null);
        }}
        templateId={templateToShare?.id}
        templateTitle={templateToShare?.title}
        source='Unlimited Sidebar'
      />
    </SidebarSection>,
    portalTarget
  );
};
